/**
 * Offers — partner offer catalogue, split into AI and Crypto sections.
 *
 * Each card is colour-coded by payout model (RevShare / CPA / Hybrid) and leads
 * with the partner's payout. "AI Lab" is a built-in offer shown in the AI
 * section alongside the API-driven offers. The selected offer is ringed.
 */
import { Link } from '@tanstack/react-router';
import { ArrowRight, BadgeCheck, Bitcoin, Coins, Layers, Percent, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge, Button, Card, PageHeader, Skeleton } from '@/components/ui';
import { partnerApi } from '@/lib/api-affiliate';
import { formatCents } from '@/lib/format';
import type { Offer } from '@/lib/types';

// ─────────────────────────────────────────────────────────────────────────
//  Built-in offers
// ─────────────────────────────────────────────────────────────────────────

/** AI Lab — flagship education offer (content sourced from the AI Lab landing). */
const AI_LAB_OFFER: Offer = {
  id: 'ai-lab',
  slug: 'ai-lab',
  name: 'AI Lab',
  description:
    'Академия AI-монетизации: трафик → аудитория → воронка → деньги. Telegram, YouTube, AI OFM, Арбитраж — первые результаты за 2–4 недели.',
  payoutModel: 'revshare',
  revsharePercent: 50,
  cpaCents: null,
  isActive: true,
};

// ─────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────

function plural(n: number, [one, few, many]: [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

type Category = 'ai' | 'crypto' | 'other';

/** Heuristic: derive an offer's vertical from its slug + name. */
function offerCategory(o: Offer): Category {
  const s = `${o.slug} ${o.name}`.toLowerCase();
  if (s.includes('crypto') || s.includes('крипт')) return 'crypto';
  if (s.includes('ai') || s.includes('нейро')) return 'ai';
  return 'other';
}

/** Payout model → colour + icon + label. Drives the whole card's accent. */
const MODEL_META: Record<Offer['payoutModel'], { label: string; color: string; Icon: LucideIcon }> = {
  revshare: { label: 'RevShare', color: 'var(--success)',       Icon: Percent },
  cpa:      { label: 'CPA',      color: 'var(--accent-bright)', Icon: Coins },
  hybrid:   { label: 'Hybrid',   color: 'var(--warning)',       Icon: Layers },
};

/** Headline payout figure + caption for an offer. */
function payoutDisplay(o: Offer): { big: string; sub: string } {
  const cpa = o.cpaCents != null && o.cpaCents > 0 ? formatCents(o.cpaCents) : null;
  const rev = o.revsharePercent != null && o.revsharePercent > 0 ? `${o.revsharePercent}%` : null;
  if (o.payoutModel === 'hybrid' && cpa && rev) return { big: `${cpa} + ${rev}`, sub: 'CPA + RevShare' };
  if (o.payoutModel === 'revshare' || (rev && !cpa)) return { big: rev ?? '—', sub: 'с каждой продажи' };
  if (o.payoutModel === 'cpa' || (cpa && !rev)) return { big: cpa ?? '—', sub: 'за конверсию' };
  return { big: cpa ?? rev ?? '—', sub: 'выплата' };
}

// ─────────────────────────────────────────────────────────────────────────
//  OfferCard
// ─────────────────────────────────────────────────────────────────────────

function OfferCard({ offer, selected }: { offer: Offer; selected: boolean }) {
  const meta = MODEL_META[offer.payoutModel] ?? MODEL_META.cpa;
  const { color, Icon } = meta;
  const payout = payoutDisplay(offer);
  const creatives = offer.creatives?.length ?? 0;

  return (
    <Card
      interactive
      className={
        'group flex flex-col overflow-hidden ' +
        (selected ? 'ring-1 ring-[var(--accent)]' : '')
      }
    >
      {/* Accent header band — colour-coded by payout model */}
      <div
        className="px-5 pt-5 pb-4"
        style={{
          background: `linear-gradient(150deg, color-mix(in oklch, ${color} 17%, var(--bg-raised)) 0%, var(--bg-raised) 72%)`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <span
            className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] shrink-0"
            style={{
              backgroundColor: `color-mix(in oklch, ${color} 16%, transparent)`,
              border: `1px solid color-mix(in oklch, ${color} 32%, transparent)`,
              color,
            }}
            aria-hidden
          >
            <Icon size={20} strokeWidth={2} />
          </span>
          {selected ? (
            <Badge tone="accent">
              <BadgeCheck size={11} strokeWidth={2.5} />
              Выбран
            </Badge>
          ) : offer.isActive ? (
            <Badge tone="success" dot>Активен</Badge>
          ) : (
            <Badge tone="warning" dot>Пауза</Badge>
          )}
        </div>
        <div
          className="mt-3 text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold"
          style={{ color }}
        >
          {meta.label}
        </div>
        <h3 className="mt-1 text-[17px] font-semibold leading-snug text-[var(--text)]">
          {offer.name || '—'}
        </h3>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col px-5 pb-5">
        <p className="text-[13px] leading-relaxed text-[var(--text-muted)] line-clamp-2 min-h-[40px]">
          {offer.description || 'Описание не задано'}
        </p>

        {/* Payout highlight — the number a partner cares about */}
        <div className="mt-4 rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--bg-base)]/40 px-4 py-3">
          <div className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
            Твоя выплата
          </div>
          <div className="mt-1 flex items-baseline gap-2 flex-wrap">
            <span
              className="text-[26px] font-bold tabular-nums leading-none text-[var(--text)]"
              style={{ letterSpacing: 'var(--tracking-tight)' }}
            >
              {payout.big}
            </span>
            <span className="text-[12px] text-[var(--text-quiet)]">{payout.sub}</span>
          </div>
        </div>

        {/* Footer — creatives note + CTA, pinned to the bottom edge */}
        <div className="mt-auto pt-4">
          {creatives > 0 && (
            <div className="mb-2.5 text-[11px] text-[var(--text-quiet)] tabular-nums">
              {creatives} {plural(creatives, ['креатив', 'креатива', 'креативов'])} в комплекте
            </div>
          )}
          <Link
            to={'/offers/$slug' as never}
            params={{ slug: offer.slug } as never}
            className="block"
          >
            <Button variant={selected ? 'secondary' : 'primary'} size="md" className="w-full">
              {selected ? 'Открыть оффер' : 'Выбрать оффер'}
              <ArrowRight
                size={15}
                strokeWidth={2.5}
                className="transition-transform duration-[var(--duration-base)] group-hover:translate-x-0.5"
              />
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Loading skeleton — mirrors the real card shape
// ─────────────────────────────────────────────────────────────────────────

function OfferCardSkeleton() {
  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between">
          <Skeleton className="h-11 w-11 rounded-[var(--radius-md)]" />
          <Skeleton className="h-5 w-20" />
        </div>
        <Skeleton className="h-3 w-16 mt-3" />
        <Skeleton className="h-5 w-2/3 mt-2" />
      </div>
      <div className="px-5 pb-5">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5 mt-2" />
        <Skeleton className="h-[68px] w-full mt-4 rounded-[var(--radius-md)]" />
        <Skeleton className="h-9 w-full mt-4" />
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  CategorySection — one vertical (AI / Crypto / Other)
// ─────────────────────────────────────────────────────────────────────────

function CategorySection({
  title,
  Icon,
  color,
  offers,
  selectedSlug,
  skeletons = 0,
}: {
  title: string;
  Icon: LucideIcon;
  color: string;
  offers: Offer[];
  selectedSlug: string | null;
  skeletons?: number;
}) {
  if (offers.length === 0 && skeletons === 0) return null;

  return (
    <section>
      <div className="mb-4 flex items-center gap-2.5">
        <span
          className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)]"
          style={{
            backgroundColor: `color-mix(in oklch, ${color} 15%, transparent)`,
            border: `1px solid color-mix(in oklch, ${color} 30%, transparent)`,
            color,
          }}
          aria-hidden
        >
          <Icon size={16} strokeWidth={2} />
        </span>
        <h2 className="text-[18px] font-semibold tracking-tight text-[var(--text)]">{title}</h2>
        {skeletons === 0 && (
          <span className="rounded-[var(--radius-pill)] bg-[var(--bg-elevated)] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-[var(--text-muted)]">
            {offers.length}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {offers.map((o) => (
          <OfferCard key={o.id} offer={o} selected={selectedSlug === o.slug} />
        ))}
        {Array.from({ length: skeletons }).map((_, i) => (
          <OfferCardSkeleton key={`sk-${i}`} />
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────────────

export function OffersPage() {
  const offers = partnerApi.offers.useList();
  const me = partnerApi.me.useGet();
  const selectedSlug = me.data?.selectedOfferSlug ?? null;
  const loading = offers.isLoading;

  // AI Lab is built-in (rich hand-crafted card). `ai-rev-50` — это его же
  // бэкенд-запись того же оффера; в каталоге показываем встроенную карточку,
  // дубль из API убираем, чтобы AI Lab не двоился.
  const apiOffers = (offers.data?.items ?? []).filter((o) => o.slug !== 'ai-rev-50');
  const all: Offer[] = [AI_LAB_OFFER, ...apiOffers];
  const ai = all.filter((o) => offerCategory(o) === 'ai');
  const crypto = all.filter((o) => offerCategory(o) === 'crypto');
  const other = all.filter((o) => offerCategory(o) === 'other');

  return (
    <div className="mx-auto max-w-[1280px]">
      <PageHeader
        eyebrow="Партнёрская программа"
        title="Каталог офферов"
        subtitle="Выберите оффер и начните лить трафик — выплаты начисляются автоматически."
        actions={
          loading ? undefined : (
            <Badge tone="neutral">
              {all.length} {plural(all.length, ['оффер', 'оффера', 'офферов'])}
            </Badge>
          )
        }
      />

      <div className="space-y-9">
        {loading ? (
          <CategorySection
            title="AI"
            Icon={Sparkles}
            color="var(--accent-bright)"
            offers={ai}
            selectedSlug={selectedSlug}
            skeletons={2}
          />
        ) : (
          <>
            <CategorySection
              title="AI"
              Icon={Sparkles}
              color="var(--accent-bright)"
              offers={ai}
              selectedSlug={selectedSlug}
            />
            <CategorySection
              title="Crypto"
              Icon={Bitcoin}
              color="var(--warning)"
              offers={crypto}
              selectedSlug={selectedSlug}
            />
            <CategorySection
              title="Другие"
              Icon={Layers}
              color="var(--text-muted)"
              offers={other}
              selectedSlug={selectedSlug}
            />
          </>
        )}
      </div>
    </div>
  );
}
